// src/lightTheme.ts
import { defaultTheme } from 'react-admin';
import { createTheme, Palette, Theme, PaletteOptions } from '@mui/material/styles';
import { BorderBottom, Height } from '@mui/icons-material';
import { max } from 'lodash';

// Declaración de módulo para extender el tema con propiedades personalizadas
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      radius: Record<string, string>;
      shadows: Record<string, string>;
      login: {
        containerGradient: (palette: Palette) => string;
        topBarGradient: (palette: Palette) => string;
        fieldBackground: (palette: Palette) => string;
        logoFilter: string;
      };
      layout: Record<string, any>;
      userMenu: Record<string, any>;
      userAvatar: Record<string, any>;
      menu: Record<string, any>;
    };
  }

  interface ThemeOptions {
    custom?: {
      radius?: Record<string, string>;
      shadows?: Record<string, string>;
      login?: {
        containerGradient?: (palette: Palette) => string;
        topBarGradient?: (palette: Palette) => string;
        fieldBackground?: (palette: Palette) => string;
        logoFilter?: string;
      };
      layout?: Record<string, any>;
      userMenu?: Record<string, any>;
      userAvatar?: Record<string, any>;
      menu?: Record<string, any>;
    };
  }

  // Extender la paleta con colores personalizados
  interface Palette {
    blue: Palette['primary'];
    white: string;
  }

  interface PaletteOptions {
    blue?: PaletteOptions['primary'];
    white?: string;
  }

  // Declarar variantes adicionales para botones
  interface ButtonPropsVariantOverrides {
    loginButton: true;
    createButton: true;
    actionButton: true;
  }

  // IconButton no tiene variantes por defecto, solo trabajaremos con sus props nativas
}

const radius = {
    small: '8px',
    medium: '12px',
    large: '24px',
    chip: '6px', 
    full: '9999px',
};

const custom = {
    radius,
    shadows: {
        none: '0 1px 2px 0 rgb(0 0 0 / 0.01)',
        subtle: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        medium: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        strong: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        login: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
        loginButton: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    },
    login: {
        containerGradient: (palette: Palette) => `linear-gradient(135deg, ${palette.grey[50]} 0%, ${palette.grey[200]} 100%)`,
        topBarGradient: (palette: Palette) => `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.primary.light} 100%)`,
        fieldBackground: (palette: Palette) => palette.grey[50],
        logoFilter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))',
    },
    layout: {
        desktopContainerPadding: '4px', 
        gap: '5px',
        mobileContainerPadding: '4px',
        mobileTopMargin: '16px',
        card: {
            borderRadius: radius.medium,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            padding: { xs: '16px', sm: '24px' } 
        }
    },
    userMenu: {
        paper: {
            width: '280px',
            borderRadius: radius.large,
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        },
    },
    userAvatar: {
        size: '40px',
        bg: '#F1F5F9',
        color: '#64748B', 
        iconSize: '24px',
    },
    menu: {
        openWidth: '230px',
        closedWidth: '64px',
        item: {
            borderRadius: radius.small,
            margin: '0',
            padding: '8px 6px',
            gap: '12px',
            color: (theme: Theme) => theme.palette.text.primary,
            hover: {
                color: (theme: Theme) => theme.palette.primary.main,
                backgroundColor: (theme: Theme) => theme.palette.action.hover,
            },
            active: {
                color: (theme: Theme) => theme.palette.primary.contrastText,
                backgroundColor: (theme: Theme) => theme.palette.primary.main,
                fontWeight: 600,
                boxShadow: (theme: Theme) => `0 4px 12px -4px ${theme.palette.primary.main}80`,
                hover: {
                    backgroundColor: (theme: Theme) => theme.palette.primary.dark,
                },
            },
            closedActiveIcon: {
                backgroundColor: (theme: Theme) => theme.palette.primary.main,
                color: (theme: Theme) => theme.palette.primary.contrastText,
            }
        }
    },
};

// Paleta de colores principal
const lightPalette: PaletteOptions = {
    mode: 'light',
    primary: { 
        main: '#0093B2', 
        light: '#53C3D2', 
        dark: '#00697A', 
        contrastText: '#FFFFFF' 
    },
    secondary: { 
        main: '#64748B', 
        light: '#94A3B8', 
        dark: '#475569', 
        contrastText: '#FFFFFF' 
    },
    background: { 
        default: '#dee9ebff', 
        paper: '#FFFFFF' 
    },
    text: { 
        primary: '#1E293B', 
        secondary: '#64748B' 
    },
    error: { 
        main: '#FB7185', 
        light: '#FDA4AF', 
        dark: '#9F1239', 
        contrastText: '#FFFFFF' 
    },
    warning: { 
        main: '#FB923C', 
        light: '#FDBA74', 
        dark: '#9A3412', 
        contrastText: '#334155' 
    },
    info: { 
        main: '#3B82F6', 
        light: '#60A5FA', 
        dark: '#1E40AF', 
        contrastText: '#FFFFFF' 
    },
    success: { 
        main: '#86be9c', 
        light: '#e3ffed', 
        dark: '#149949ff', 
        contrastText: '#FFFFFF' 
    },
    divider: '#E2E8F0',
    grey: { 
        50: '#F8FAFC', 
        100: '#F1F5F9', 
        200: '#E2E8F0', 
        300: '#CBD5E1', 
        400: '#94A3B8', 
        500: '#64748B', 
        600: '#475569', 
        700: '#334155', 
        800: '#1E293B', 
        900: '#0F172A' 
    },
    blue: { 
        50: '#EFF6FF', 
        100: '#DBEAFE', 
        200: '#BFDBFE', 
        300: '#93C5FD', 
        400: '#60A5FA', 
        500: '#3B82F6', 
        600: '#2563EB', 
        700: '#1D4ED8', 
        800: '#1E40AF', 
        900: '#1E3A8A' 
    },
    white: '#FFFFFF',
};

// Crear el tema base primero
const baseTheme = createTheme({
    ...defaultTheme,
    palette: lightPalette,
    custom: custom,
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14, 
        htmlFontSize: 16,
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.4,
        },
    },
});

// Ahora crear el tema final con los componentes que usan el tema completo
export const lightTheme = createTheme({
    ...baseTheme,
    components: {
        MuiCssBaseline: {
            styleOverrides: {

                '.RaList-main': {
                    '& .RaList-actions': {
                        borderBottom: `1px solid ${baseTheme.palette.divider}`,

                        
                    },
                },
            },
        },
      
      




        MuiButton: {
            defaultProps: {
                size: 'small' as const,
            },
            styleOverrides: {
                root: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontWeight: 500,
                    borderRadius: radius.small,
                    textTransform: 'none',
                    padding: '8px 16px',
                    transition: 'background-color 0.3s ease, color 0.3s ease',
                }
            },
            variants: [
                {
                    props: { variant: 'loginButton' },
                    style: {
                        borderRadius: radius.small,
                        padding: baseTheme.spacing(1.5),
                        color: baseTheme.palette.primary.contrastText,
                        fontSize: '1rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: baseTheme.palette.primary.main,
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: 'none',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: custom.shadows.loginButton,
                            backgroundColor: baseTheme.palette.primary.light,
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                    }
                },
                {
                    props: { variant: 'createButton' },
                    style: {
                        backgroundColor: baseTheme.palette.primary.main,
                        color: '#FFFFFF',
                        boxShadow: 'none',
                        padding: '6px 16px',
                        fontWeight: 500,
                        fontSize: '1rem',
                        minWidth: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        '&:hover': {
                            backgroundColor: baseTheme.palette.primary.light,
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem',
                            color: '#FFFFFF',
                        },
                        [baseTheme.breakpoints.down('sm')]: {
                            position: 'fixed',
                            right: '16px',
                            bottom: '16px',
                            width: '56px',
                            height: '56px',
                            borderRadius: radius.full,
                            padding: 0,
                            minWidth: '56px',
                            zIndex: 1300,
                            backgroundColor: baseTheme.palette.primary.main,
                            boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
                            justifyContent: 'center',
                            '& .MuiButton-startIcon': {
                                margin: 0,
                            }
                        }
                    }
                },
                {
                    props: { variant: 'actionButton' },
                    style: {
                        minWidth: 'auto',
                        width: '32px',
                        height: '32px',
                        color: '#495057',
                        border: '1px solid #e9ecef',
                        backgroundColor: '#f8f9fa',
                        padding: baseTheme.spacing(0.5, 1),
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.3s ease, color 0.3s ease',
                        '&:hover': {
                            backgroundColor: '#e9ecef',
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem',
                        },
                        // Responsive usando media queries correctas
                        [baseTheme.breakpoints.down('sm')]: {
                            width: '40px',
                            height: '40px',
                            padding: baseTheme.spacing(1),
                        },
                    },
                },
            ]
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    border: `1px solid ${baseTheme.palette.primary.dark}20`, 
                    borderRadius: 6, 
                    color: baseTheme.palette.primary.main,
                    width: 36,
                    height: 32,
                    '&:hover': {
                        backgroundColor: baseTheme.palette.primary.dark, 
                        color: baseTheme.palette.common.white,
                    },
                },
                // Estilos específicos para diferentes colores
                colorInherit: {
                    color: baseTheme.palette.text.secondary,
                    border: `1px solid ${baseTheme.palette.text.secondary}20`,
                    '&:hover': {
                        color: baseTheme.palette.primary.main,
                        backgroundColor: baseTheme.palette.action.hover,
                        border: `1px solid ${baseTheme.palette.primary.main}30`,
                    }
                },
                colorPrimary: {
                    color: baseTheme.palette.primary.main,
                    border: `1px solid ${baseTheme.palette.primary.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.primary.main}14`,
                    }
                },
                colorSecondary: {
                    color: baseTheme.palette.secondary.main,
                    border: `1px solid ${baseTheme.palette.secondary.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.secondary.main}14`,
                    }
                },
                colorError: {
                    color: baseTheme.palette.error.main,
                    border: `1px solid ${baseTheme.palette.error.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.error.main}14`,
                    }
                },
                colorWarning: {
                    color: baseTheme.palette.warning.main,
                    border: `1px solid ${baseTheme.palette.warning.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.warning.main}14`,
                    }
                },
                colorInfo: {
                    color: baseTheme.palette.info.main,
                    border: `1px solid ${baseTheme.palette.info.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.info.main}14`,
                    }
                },
                colorSuccess: {
                    color: baseTheme.palette.success.main,
                    border: `1px solid ${baseTheme.palette.success.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.success.main}14`,
                    }
                },
                // Clases personalizadas que puedes usar con className
                sizeLarge: {
                    width: 48,
                    height: 48,
                },
                sizeSmall: {
                    width: 32,
                    height: 32,
                },
            },
        },

         MuiBadge: {
            defaultProps: {
                color: 'primary',
                max: 99,
                anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                // Si lo usas sobre Avatars/IconButtons, quizá prefieras:
                // overlap: 'circular',
            },
            styleOverrides: {
                // Estiliza el "píldora" del badge
                badge: ({ theme }) => ({
                borderRadius: theme.custom?.radius?.chip ?? '6px', // cuadrado con esquinas redondeadas
                minWidth: 18,
                height: 18,
                padding: '0 4px', // permite 2+ dígitos sin deformar
                border: `2px solid ${theme.palette.common.white}`,
                backgroundColor: theme.palette.primary.dark,
                color: theme.palette.common.white,
                // Opcional: fuerza números centrados verticalmente
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                lineHeight: 1,
                }),
            },
            },

        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '16px',
                    fontWeight: 500,
                    minWidth: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    '& .MuiButton-text': {  
                        backgroundColor: '#f8f9fa',
                        color: '#495057',
                        border: '1px solid #e9ecef',
                        '&:hover': {
                            backgroundColor: '#e9ecef',
                        }
                    },
                    '& .MuiButton-contained': {  
                        backgroundColor: baseTheme.palette.primary.main,
                        color: '#FFFFFF',
                        border: `1px solid ${baseTheme.palette.primary.main}`, 
                        '&:hover': {
                            backgroundColor: baseTheme.palette.primary.dark,
                            borderColor: baseTheme.palette.primary.dark, 
                        }
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem',
                    },
                }
            }
        },
        RaList: {
        styleOverrides: {
                root: {
                    '& .RaList-main': {
                        paddingTop: 16, 
                        paddingLeft: 16,
                        paddingRight: 16,
                    }
                }
            }
        },
        RaTopToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent', 
                    marginBottom: baseTheme.spacing(2), 
                },
            },
        },

        RaDatagrid: {
            styleOverrides: {
                root: {
                    backgroundColor: baseTheme.palette.background.paper,
                    border: `1px solid ${baseTheme.palette.divider}`,
                    borderRadius: radius.medium,
                    overflow: 'hidden',

                    '& .RaDatagrid-headerCell': {
                        backgroundColor: `${baseTheme.palette.primary.dark}10`,
                        borderBottom: `2px solid ${baseTheme.palette.divider}`,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '12px',
                        color: baseTheme.palette.text.primary,
                    },

                    '& .RaDatagrid-row': {
                        backgroundColor: baseTheme.palette.common.white,
                        transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s',
                        '&:hover': {
                            backgroundColor: baseTheme.palette.action.hover,
                        },
                    },

                    '& .RaDatagrid-rowCell': {
                        fontSize: '0.875rem',
                        padding: '10px 12px',
                        borderBottom: `1px solid ${baseTheme.palette.divider}`,
                        color: baseTheme.palette.text.primary,
                        '&:last-of-type': {
                            borderRight: 'none',
                        },
                    },

                    'tbody tr:last-child .RaDatagrid-rowCell': {
                        borderBottom: 'none',
                    },

                    '& .MuiTablePagination-root': {
                        borderTop: `1px solid ${baseTheme.palette.divider}`,
                        backgroundColor: baseTheme.palette.grey[50],
                        padding: baseTheme.spacing(1),
                    },

                    '& .RaDatagrid-rowCell:has(button)': {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                    },

                    '& button.RaButton-link': {
                        fontSize: '0.8rem',
                        color: baseTheme.palette.text.secondary,
                        padding: '4px 8px',
                        borderRadius: radius.small,
                        transition: 'color 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                            color: baseTheme.palette.primary.main,
                            backgroundColor: `${baseTheme.palette.primary.light}10`,
                        },
                    },

                    '& button[aria-label="Delete"]': {
                        color: baseTheme.palette.primary.main,
                        width: '36',
                        '&:hover': {
                            color: baseTheme.palette.common.white,
                            backgroundColor: baseTheme.palette.error.main,
                        },
                    },
                },
            },
        },

        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: baseTheme.palette.common.white,
                    borderRadius: radius.medium,
                    boxShadow: 'none', 
                    border: 'none', 
                    marginBottom: baseTheme.spacing(2),
                },
            },
        },
        
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '12px 16px',
                }
            }
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: radius.chip,
                    fontWeight: 500,
                    // Estilo por defecto (cuando no tiene color específico)
                    backgroundColor: baseTheme.palette.grey[200], 
                    color: baseTheme.palette.grey[700],
                },
                colorPrimary: {
                    backgroundColor: `${baseTheme.palette.primary.main}20`,
                    color: baseTheme.palette.primary.dark,
                },
                colorSecondary: {
                    backgroundColor: `${baseTheme.palette.secondary.main}20`,
                    color: baseTheme.palette.secondary.dark,
                },
                colorSuccess: {
                    backgroundColor: `${baseTheme.palette.success.main}20`,
                    color: baseTheme.palette.success.dark,
                },
                colorWarning: {
                    backgroundColor: `${baseTheme.palette.warning.main}20`,
                    color: baseTheme.palette.warning.dark,
                },
                colorError: {
                    backgroundColor: `${baseTheme.palette.error.main}20`,
                    color: baseTheme.palette.error.dark,
                },
                colorInfo: {
                    backgroundColor: `${baseTheme.palette.info.main}20`,
                    color: baseTheme.palette.info.dark,
                },
            },
        },

        MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small', // Si quieres que sea el tamaño por defecto
      },
      styleOverrides: {
        root: {
          // Asegurar altura consistente
          '& .MuiInputBase-root': {
            minHeight: '48px', // Altura mínima consistente
          },
        },
      },
    },
     MuiSelect: {
      styleOverrides: {
        select: {
          // Alinear el contenido del select igual que los text inputs
          display: 'flex',
          alignItems: 'center',
          minHeight: '22px', // Altura mínima del contenido
          paddingTop: '8.5px',
          paddingBottom: '8.5px',
        },
        icon: {
          // Posicionar el icono de dropdown consistentemente
          top: 'calc(50% - 12px)',
        },
      },
    },
      MuiInputBase: {
      styleOverrides: {
        root: {
          // Altura consistente para todos los inputs
          '&.MuiInputBase-sizeSmall': {
            minHeight: '48px',
          },
          // Para inputs normales
          '&:not(.MuiInputBase-sizeSmall)': {
            minHeight: '56px',
          },
        },
        input: {
          // Alineación vertical consistente del texto
          '&.MuiInputBase-inputSizeSmall': {
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
          },
        },
      },
    },
    
    // Configuración para los labels (InputLabel)
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // Posición consistente del label
          '&.MuiInputLabel-sizeSmall': {
            transform: 'translate(14px, 9px) scale(1)',
          },
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },
    
    // Configuración para FormControl (contenedor de los inputs)
    MuiFormControl: {
      styleOverrides: {
        root: {
          // Asegurar que todos los form controls tengan el mismo comportamiento
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
    
    // Configuración específica para Autocomplete (si lo usas)
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          paddingTop: '4px',
          paddingBottom: '4px',
          '&.MuiInputBase-sizeSmall': {
            paddingTop: '2px',
            paddingBottom: '2px',
            '& .MuiAutocomplete-input': {
              paddingTop: '6.5px',
              paddingBottom: '6.5px',
            },
          },
        },
      },
    },
    
    // Para los helper texts (mantenerlos alineados)
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '3px',
          marginLeft: '14px',
          marginRight: '14px',
        },
      },
    },
  },
});

export default lightTheme;